import { QrBorderVariant, getQrBorderSpec, isQrBorderVariant } from './qr-border-styles';

const exportScale = 3;

export function qrDownloadName(kind: string, channelName: string): string {
  const mapped = kind === 'quick-links' ? 'quicklinks' : kind;
  const safeKind = mapped.replace(/[^a-zA-Z0-9._-]+/g, '').slice(0, 80) || 'qr';
  const safe = channelName.replace(/[^a-zA-Z0-9._-]+/g, '').slice(0, 80) || 'profile';
  return `${safeKind}-${safe}.png`;
}

export type QrExportGeometry = {
  exportScale: number;
  svgOutput: number;
  borderPx: number;
  midBandPx: number;
  quietPx: number;
  contentOffset: number;
  canvasSize: number;
  variant: QrBorderVariant;
};

export function resolveQrExportGeometry(
  wrapper: HTMLElement,
  svgCssWidth: number,
  scale = exportScale,
): QrExportGeometry {
  const variantAttr = wrapper.getAttribute('data-qr-border');
  if (!variantAttr || !isQrBorderVariant(variantAttr)) {
    throw new Error('missing qr border wrapper');
  }
  const spec = getQrBorderSpec(variantAttr);
  const borderPx = Math.round(spec.borderWidthPx * scale);
  const midBandPx = Math.round((spec.midBandWidthPx ?? 0) * scale);
  const quietPx = Math.round(spec.quietZonePx * scale);
  const contentOffset = borderPx + midBandPx + quietPx;
  const svgOutput = Math.round(svgCssWidth * scale);
  return {
    exportScale: scale,
    svgOutput,
    borderPx,
    midBandPx,
    quietPx,
    contentOffset,
    canvasSize: svgOutput + (2 * contentOffset),
    variant: variantAttr,
  };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('image'));
    image.src = src;
  });
}

async function loadCenter(src: string): Promise<HTMLImageElement> {
  if (src.startsWith('data:') || src.startsWith('blob:')) return loadImage(src);
  const absolute = new URL(src, window.location.href);
  if (absolute.origin === window.location.origin) return loadImage(src);
  const response = await fetch(src);
  if (!response.ok) throw new Error('photo');
  const blobUrl = URL.createObjectURL(await response.blob());
  try {
    return await loadImage(blobUrl);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

function radiusPx(value: string, outputSize: number, scale: number): number {
  if (value.endsWith('%')) return (Number.parseFloat(value) / 100) * outputSize;
  if (value.endsWith('px')) return Number.parseFloat(value) * scale;
  return 0;
}

function cornerRadii(
  radius: string,
  width: number,
  height: number,
  scale: number,
): { rx: number; ry: number } {
  const parts = radius.split(/\s+/).filter(Boolean);
  const horizontal = parts[0] || '0';
  const vertical = parts[1] || horizontal;
  return {
    rx: radiusPx(horizontal, width, scale),
    ry: radiusPx(vertical, height, scale),
  };
}

function traceRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.min(Math.max(radius, 0), width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function clipCenter(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  rx: number,
  ry: number,
): void {
  const radiusX = Math.min(Math.max(rx, 0), width / 2);
  const radiusY = Math.min(Math.max(ry, 0), height / 2);
  ctx.beginPath();
  if (radiusX >= width / 2 - 0.5 && radiusY >= height / 2 - 0.5) {
    ctx.ellipse(
      x + width / 2,
      y + height / 2,
      width / 2,
      height / 2,
      0,
      0,
      Math.PI * 2,
    );
  } else {
    ctx.moveTo(x + radiusX, y);
    ctx.lineTo(x + width - radiusX, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radiusY);
    ctx.lineTo(x + width, y + height - radiusY);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radiusX, y + height);
    ctx.lineTo(x + radiusX, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radiusY);
    ctx.lineTo(x, y + radiusY);
    ctx.quadraticCurveTo(x, y, x + radiusX, y);
  }
  ctx.closePath();
  ctx.clip();
}

function drawFitted(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
  fit: string,
): void {
  const imageScale = fit === 'cover'
    ? Math.max(width / image.width, height / image.height)
    : Math.min(width / image.width, height / image.height);
  const drawnWidth = image.width * imageScale;
  const drawnHeight = image.height * imageScale;
  ctx.drawImage(
    image,
    x + (width - drawnWidth) / 2,
    y + (height - drawnHeight) / 2,
    drawnWidth,
    drawnHeight,
  );
}

function drawWrapperBackground(
  ctx: CanvasRenderingContext2D,
  geometry: QrExportGeometry,
  wrapper: HTMLElement,
  wrapperStyle: CSSStyleDeclaration,
): void {
  const spec = getQrBorderSpec(geometry.variant);
  const stroke = wrapperStyle.borderTopColor;
  const radiusCss = Number.parseFloat(wrapperStyle.borderTopLeftRadius) || spec.borderRadiusPx;
  const radiusPxScaled = Math.round(radiusCss * geometry.exportScale);
  const { canvasSize, borderPx, midBandPx } = geometry;
  const scale = geometry.exportScale;

  if (geometry.variant === 'quietZoneFramed' && midBandPx > 0) {
    const quietInner = wrapper.querySelector('[data-qr-quiet-inner]');
    const quietStyle = quietInner instanceof HTMLElement
      ? getComputedStyle(quietInner)
      : wrapperStyle;
    const midFill = wrapperStyle.backgroundColor;
    const quietFill = quietStyle.backgroundColor;
    const innerRadiusCss = Number.parseFloat(quietStyle.borderTopLeftRadius)
      || Math.max(spec.borderRadiusPx - (spec.midBandWidthPx ?? 0), 0);
    const innerRadiusPx = Math.round(innerRadiusCss * scale);

    if (midFill && midFill !== 'rgba(0, 0, 0, 0)') {
      const midInset = borderPx;
      const midSize = canvasSize - (2 * midInset);
      traceRoundedRect(ctx, midInset, midInset, midSize, midSize, Math.max(radiusPxScaled - midInset, 0));
      ctx.fillStyle = midFill;
      ctx.fill();
    }

    if (quietFill && quietFill !== 'rgba(0, 0, 0, 0)') {
      const quietInset = borderPx + midBandPx;
      const quietSize = canvasSize - (2 * quietInset);
      traceRoundedRect(ctx, quietInset, quietInset, quietSize, quietSize, Math.max(innerRadiusPx, 0));
      ctx.fillStyle = quietFill;
      ctx.fill();
    }
  } else {
    const fill = wrapperStyle.backgroundColor;
    if (fill && fill !== 'rgba(0, 0, 0, 0)') {
      traceRoundedRect(ctx, 0, 0, canvasSize, canvasSize, radiusPxScaled);
      ctx.fillStyle = fill;
      ctx.fill();
    }
  }

  if (borderPx > 0 && stroke && stroke !== 'rgba(0, 0, 0, 0)') {
    const inset = borderPx / 2;
    const strokeSize = canvasSize - borderPx;
    traceRoundedRect(ctx, inset, inset, strokeSize, strokeSize, Math.max(radiusPxScaled - inset, 0));
    ctx.strokeStyle = stroke;
    ctx.lineWidth = borderPx;
    ctx.stroke();
  }
}

export async function downloadQrPng(frame: HTMLElement, filename: string): Promise<void> {
  const svg = frame.querySelector('svg');
  const mark = frame.querySelector('img');
  if (!svg || !mark) throw new Error('missing qr');
  const wrapper = svg.closest('[data-qr-border]');
  if (!wrapper || !(wrapper instanceof HTMLElement)) throw new Error('missing qr border wrapper');
  const svgRect = svg.getBoundingClientRect();
  const markRect = mark.getBoundingClientRect();
  if (svgRect.width < 1) throw new Error('qr size');

  const geometry = resolveQrExportGeometry(wrapper, svgRect.width);
  const wrapperStyle = getComputedStyle(wrapper);

  const clone = svg.cloneNode(true) as SVGSVGElement;
  const livePaths = svg.querySelectorAll('path');
  const clonePaths = clone.querySelectorAll('path');
  livePaths.forEach((path, index) => {
    const fill = getComputedStyle(path).fill;
    if (clonePaths[index] && fill) clonePaths[index].setAttribute('fill', fill);
  });
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(geometry.svgOutput));
  clone.setAttribute('height', String(geometry.svgOutput));

  const svgUrl = URL.createObjectURL(new Blob(
    [new XMLSerializer().serializeToString(clone)],
    { type: 'image/svg+xml;charset=utf-8' },
  ));
  try {
    const svgImage = await loadImage(svgUrl);
    const center = await loadCenter(mark.currentSrc || mark.src);
    const canvas = document.createElement('canvas');
    canvas.width = geometry.canvasSize;
    canvas.height = geometry.canvasSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas');

    drawWrapperBackground(ctx, geometry, wrapper, wrapperStyle);
    ctx.drawImage(svgImage, geometry.contentOffset, geometry.contentOffset, geometry.svgOutput, geometry.svgOutput);

    const scale = geometry.svgOutput / svgRect.width;
    const x = geometry.contentOffset + ((markRect.left - svgRect.left) * scale);
    const y = geometry.contentOffset + ((markRect.top - svgRect.top) * scale);
    const width = markRect.width * scale;
    const height = markRect.height * scale;
    const style = getComputedStyle(mark);
    const radii = cornerRadii(style.borderRadius, width, height, scale);
    ctx.save();
    clipCenter(ctx, x, y, width, height, radii.rx, radii.ry);
    drawFitted(ctx, center, x, y, width, height, style.objectFit);
    ctx.restore();

    const borderCss = Number.parseFloat(style.borderTopWidth) || 0;
    const borderW = borderCss * scale;
    if (borderW > 0 && style.borderTopStyle !== 'none') {
      const strokeColor = style.borderTopColor;
      if (strokeColor && strokeColor !== 'rgba(0, 0, 0, 0)') {
        const inset = borderW / 2;
        const strokeRadii = cornerRadii(style.borderRadius, width - borderW, height - borderW, scale);
        ctx.save();
        traceRoundedRect(
          ctx,
          x + inset,
          y + inset,
          width - borderW,
          height - borderW,
          Math.max(strokeRadii.rx, 0),
        );
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = borderW;
        ctx.stroke();
        ctx.restore();
      }
    }

    const png = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
    if (!png) throw new Error('png');
    const downloadUrl = URL.createObjectURL(png);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(downloadUrl);
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}
